import { core } from '../core/index.js';
import { filterFabricErrors } from '../errors/errors.js';
import {
  type AnnotationItem,
  type DeselectAnntationsParam,
  type DeselectAnntationsResult,
  type FabricMapOperationEventType,
  type GetAllAnnotationsParam,
  type GetAllAnnotationsResult,
  type GetCoordinateDirectionParam,
  type GetCoordinateDirectionResult,
  type GetCoordinateScreenDistanceParam,
  type GetCoordinateScreenDistanceResult,
  type GetCoordinateScreenPositionParam,
  type GetCoordinateScreenPositionResult,
  type MapSupplierCoordinateExchangeParam,
  type MapSupplierCoordinateExchangeResult,
  type RemoveAnnotationsParam,
  type RemoveAnnotationsResult,
  type SelectAnntationsParam,
  type SelectAnntationsResult,
  type SetMapAddPathsParam,
  type SetMapCenterCoordinateParam,
  type SetMapExchangePathParam,
  type SetMapGetAllPathsParam,
  type SetMapGetAllPathsResult,
  type SetMapPathPlanningParam,
  type SetMapPathPlanningResult,
  type SetMapRemovePathsParam,
  type SetMapScreenViewportAreaParam,
  type SetMapScreenViewportCenterParam,
  type SetMapUpdatePathsParam,
  type SetMapZoomParam,
  type SetMapZoomParamResult,
  type SetUserLocationRepresentationParam,
  type UpdateAnnotationsParam,
  type UpdateAnnotationsResult,
  type UpdateFlagstickParam,
  type UpdateFlagstickResult,
} from './map-operation-api/index.js';

/**
 * 设置地图事件.
 */
export type FabricMapOptions<T extends FabricMapOperationEventType> = {
  type: T;
  param: T extends 'setMapScreenViewportCenter'
    ? SetMapScreenViewportCenterParam
    : T extends 'setMapScreenViewportArea'
      ? SetMapScreenViewportAreaParam
      : T extends 'setMapZoom'
        ? SetMapZoomParam
        : T extends 'setUserLocationRepresentation'
          ? SetUserLocationRepresentationParam
          : T extends 'setMapCenterCoordinate'
            ? SetMapCenterCoordinateParam
            : T extends 'removeAnnotations'
              ? RemoveAnnotationsParam
              : T extends 'updateAnnotations'
                ? UpdateAnnotationsParam
                : T extends 'getAllAnnotations'
                  ? GetAllAnnotationsParam
                  : T extends 'selectAnntations'
                    ? SelectAnntationsParam
                    : T extends 'deselectAnntations'
                      ? DeselectAnntationsParam
                      : T extends 'updateFlagstick'
                        ? UpdateFlagstickParam
                        : T extends 'mapSupplierCoordinateExchange'
                          ? MapSupplierCoordinateExchangeParam
                          : T extends 'coordinateDirection'
                            ? GetCoordinateDirectionParam
                            : T extends 'coordinateScreenPosition'
                              ? GetCoordinateScreenPositionParam
                              : T extends 'coordinateScreenDistance'
                                ? GetCoordinateScreenDistanceParam
                                : T extends 'setMapPathPlanning'
                                  ? SetMapPathPlanningParam
                                  : T extends 'addPaths'
                                    ? SetMapAddPathsParam
                                    : T extends 'removePaths'
                                      ? SetMapRemovePathsParam
                                      : T extends 'getAllPaths'
                                        ? SetMapGetAllPathsParam
                                        : T extends 'updatePaths'
                                          ? SetMapUpdatePathsParam
                                          : T extends 'exchangePath'
                                            ? SetMapExchangePathParam
                                            : undefined;
};

export type FabricMapResult<T extends FabricMapOperationEventType> =
  T extends 'setMapZoom'
    ? SetMapZoomParamResult
    : T extends 'getAllAnnotations'
      ? GetAllAnnotationsResult
      : T extends 'mapSupplierCoordinateExchange'
        ? MapSupplierCoordinateExchangeResult
        : T extends 'updateAnnotations'
          ? UpdateAnnotationsResult
          : T extends 'updateFlagstick'
            ? UpdateFlagstickResult
            : T extends 'selectAnntations'
              ? SelectAnntationsResult
              : T extends 'removeAnnotations'
                ? RemoveAnnotationsResult
                : T extends 'deselectAnntations'
                  ? DeselectAnntationsResult
                  : T extends 'coordinateDirection'
                    ? GetCoordinateDirectionResult
                    : T extends 'coordinateScreenDistance'
                      ? GetCoordinateScreenDistanceResult
                      : T extends 'coordinateScreenPosition'
                        ? GetCoordinateScreenPositionResult
                        : T extends 'setMapPathPlanning'
                          ? SetMapPathPlanningResult
                          : T extends 'getAllPaths'
                            ? SetMapGetAllPathsResult
                            : undefined;

export type FabricMapApi = <T extends FabricMapOperationEventType>(
  options: FabricMapOptions<T>
) => Promise<FabricMapResult<T>>;

/**
 * 调用地图API setMapViewportCenter, ....
 * @example
 * ```ts
 * const result = await setMap({
 *   type: 'setMapViewportCenter',
 *   param: {
 *      point: {
 *        x: 0,
 *        y: 0,
 *      },
 *   },
 * });
 * ```
 */
export const setMap = <T extends FabricMapOperationEventType>(
  options: FabricMapOptions<T>
): Promise<FabricMapResult<T>> => {
  return new Promise<FabricMapResult<T>>((resolve, reject) => {
    const newOptions: FabricMapOptions<T> = {
      ...options,
    };
    // 旗杆大头针的参数转移
    if (options.type === 'updateFlagstick') {
      const flatstickParam: UpdateAnnotationsParam = {
        annotations: [
          { ...options.param, isLockedToScreen: true } as AnnotationItem,
        ],
      };
      // Native 只处理updateAnnotations, 无updateFlagstick定义.
      newOptions.type = 'updateAnnotations' as T;
      newOptions.param = flatstickParam as FabricMapOptions<T>['param'];
    }

    core.call<'fabric_map.setMap', FabricMapOptions<T>, FabricMapResult<T>>(
      'fabric_map.setMap',
      newOptions,
      (result) => {
        const fabricErrors = filterFabricErrors(
          result.code,
          result.message,
          result.data
        );
        if (fabricErrors) {
          reject(fabricErrors);
        } else {
          resolve(result.data);
        }
      }
    );
  });
};
